import React, { useEffect } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { FiTrash2 } from 'react-icons/fi';
import { DOMINIO } from '../../api/apiConfig';
import { ResourceField, ResourceFormPage } from '../../components/resource/ResourceFormPage';
import { ordensManutencaoService, maquinasService } from '../../services';
import { OrdemManutencao, OrdemManutencaoFoto } from '../../types/manutencao';
import { makeOptionLoader } from '../../utils/optionLoaders';

const fields: ResourceField<OrdemManutencao>[] = [
  {
    name: 'maquina',
    label: 'Máquina',
    type: 'async-select',
    valueType: 'number',
    loadOptions: makeOptionLoader(maquinasService.list, (item) => item.nome ?? String(item.id)),
    showSearchControl: false,
  },
  {
    name: 'tipo_manutencao',
    label: 'Tipo de manutenção',
    type: 'select',
    options: [
      { label: 'Preventiva', value: 'PREVENTIVA' },
      { label: 'Corretiva', value: 'CORRETIVA' },
      { label: 'Preditiva', value: 'PREDITIVA' },
      { label: 'Emergencial', value: 'EMERGENCIAL' },
    ],
  },
  {
    name: 'prioridade',
    label: 'Prioridade',
    type: 'select',
    options: [
      { label: 'Baixa', value: 'BAIXA' },
      { label: 'Média', value: 'MEDIA' },
      { label: 'Alta', value: 'ALTA' },
      { label: 'Urgente', value: 'URGENTE' },
    ],
  },
  { name: 'data_inicio', label: 'Data de início', type: 'date' },
  { name: 'data_fim', label: 'Data de fim', type: 'date' },
  { name: 'responsavel', label: 'Responsável' },
  { name: 'tempo_previsto_execucao', label: 'Tempo previsto (h)', type: 'number' },
  { name: 'custo_estimado', label: 'Custo estimado', type: 'number' },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { label: 'Aberta', value: 'ABERTA' },
      { label: 'Em andamento', value: 'EM_ANDAMENTO' },
      { label: 'Concluída', value: 'CONCLUIDA' },
      { label: 'Cancelada', value: 'CANCELADA' },
    ],
  },
  { name: 'descricao_falha', label: 'Descrição da falha', type: 'textarea' },
];

const normalizeFotos = (value: unknown): OrdemManutencaoFoto[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<OrdemManutencaoFoto[]>((acc, item) => {
    if (!item || typeof item !== 'object') {
      return acc;
    }

    const foto = item as Partial<OrdemManutencaoFoto>;
    const id = Number(foto.id);

    if (!Number.isFinite(id) || !foto.imagem) {
      return acc;
    }

    acc.push({
      id,
      imagem: String(foto.imagem),
      descricao: String(foto.descricao ?? ''),
      data_upload: foto.data_upload,
    });

    return acc;
  }, []);
};

const resolveImageUrl = (url: string) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  return `${DOMINIO}${url.startsWith('/') ? '' : '/'}${url}`;
};

interface FotosSectionProps {
  record: Partial<OrdemManutencao> | null;
  fotosValue: unknown;
  setFieldValue: (name: string, value: unknown) => void;
}

const OrdemFotosSection: React.FC<FotosSectionProps> = ({ record, fotosValue, setFieldValue }) => {
  const recordId = Number(record?.id);
  const hasFotosValue = Array.isArray(fotosValue);
  const fotos = normalizeFotos(fotosValue);
  const fotosRecord = normalizeFotos(record?.fotos);

  useEffect(() => {
    if (!recordId) {
      return;
    }

    if (hasFotosValue || fotosRecord.length === 0) {
      return;
    }

    setFieldValue('fotos', fotosRecord);
  }, [recordId, hasFotosValue, fotosRecord, setFieldValue]);

  if (!recordId) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <h6 className="mb-1">Fotos da manutenção</h6>
          <small className="text-muted">As fotos serão exibidas após salvar a ordem e voltar para edição.</small>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h6 className="mb-1">Fotos da manutenção</h6>
            <small className="text-muted">Edite descrição ou remova fotos antes de atualizar a ordem.</small>
          </div>
          <span className="badge bg-light text-dark border">{fotos.length} foto(s)</span>
        </div>

        {fotos.length === 0 ? (
          <div className="text-muted small">Nenhuma foto vinculada a esta ordem.</div>
        ) : (
          <Row className="g-3">
            {fotos.map((foto) => (
              <Col key={foto.id} xs={12} md={6} xl={4}>
                <Card className="h-100 border">
                  <Card.Img
                    variant="top"
                    src={resolveImageUrl(foto.imagem)}
                    alt={`Foto ${foto.id} da ordem`}
                    style={{ aspectRatio: '4 / 3', objectFit: 'cover' }}
                  />
                  <Card.Body className="d-flex flex-column gap-2">
                    <Form.Group>
                      <Form.Label className="small text-muted mb-1">Descrição</Form.Label>
                      <Form.Control
                        size="sm"
                        value={foto.descricao ?? ''}
                        placeholder="Sem descrição"
                        onChange={(event) => {
                          const next = fotos.map((item) =>
                            item.id === foto.id ? { ...item, descricao: event.target.value } : item,
                          );
                          setFieldValue('fotos', next);
                        }}
                      />
                    </Form.Group>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">ID {foto.id}</small>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          setFieldValue(
                            'fotos',
                            fotos.filter((item) => item.id !== foto.id),
                          );
                        }}
                      >
                        <FiTrash2 className="me-1" />
                        Excluir
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card.Body>
    </Card>
  );
};

export const OrdensManutencaoFormPage: React.FC = () => {
  return (
    <ResourceFormPage
      title="Nova ordem de manutenção"
      description="Planeje intervenções nas máquinas."
      fields={fields}
      service={ordensManutencaoService}
      redirectPath="/admin/manutencao/ordens-manutencao"
      cancelPath="/admin/manutencao/ordens-manutencao"
      renderAfterRecord={(record, formData, setFieldValue) => (
        <OrdemFotosSection
          record={record}
          fotosValue={formData.fotos ?? record?.fotos}
          setFieldValue={setFieldValue}
        />
      )}
    />
  );
};
